import { Graphics } from "pixi.js";
import { ISO_TILE_H, ISO_TILE_W } from "@/lib/pixi/isoCoords";

/**
 * プロシージャル描画ユーティリティ。
 * ビットマップを使わず、Pixi の Graphics で機械・床・ワーク等を直接描く。
 * ポップなパレット＋角丸＋ソフトシャドウ風の見た目を目指す。
 */

export const PALETTE = {
  groundLight: 0xe8e3d7,
  groundDark: 0xd4ccbb,
  groundAccent: 0xbfb59f,
  lineAccent: 0x5a524a,
  conveyorBase: 0x3a3a46,
  conveyorStripe: 0xf6c03a,
  conveyorEdge: 0x242430,
  // ダークパープル/ネイビー系のコンベア（参照: conveyor-stripe.png）
  conveyorSideTop: 0x6c6c92, // サイドパネル上端のハイライト（明るいパープル）
  conveyorSideMain: 0x4f4f74, // サイドパネル本体
  conveyorSideShadow: 0x39395a, // 右側面（影側）
  conveyorBeltRim: 0x2a2a44, // ベルト天面の縁（やや暗いパープル）
  conveyorBelt: 0x33334d, // ベルト本体（暗パープル）
  conveyorBeltLight: 0x55557a, // ベルト中央のハイライト（薄パープル）
  pressBase: 0xff7a3d,
  pressDark: 0xc45520,
  pressHead: 0x2b2a33,
  pressGlow: 0x2ec4b6,
  armBase: 0xff7a3d,
  armJoint: 0x2b2a33,
  armGrip: 0x5c5a66,
  robotBase: 0xfdd835,
  robotDark: 0xa67c00,
  robotCab: 0x2b2a33,
  workIdle: 0xff4d6d,
  workDone: 0x70c1b3,
  workPressed: 0x9b5de5,
  unloadZone: 0x2ec4b6,
  // 占有タイルの色（機械ごとに固有色）
  occupationPress: 0xff7a3d, // オレンジ（プレス）
  occupationArm: 0xe63946, // レッド（アーム）
  occupationLoading: 0xfdd835, // イエロー（積み込み位置 = 搬送ロボ待機）
  occupationUnloading: 0x4ea5ff, // ライトブルー（積み下ろし位置 = 搬送ロボの停止場所）
  occupationStorage: 0x2ec4b6, // ティール（積み下ろした荷物の保管場所 = フェードアウト先）
  occupationBuffer: 0x9c27b0, // パープル（コンベア終端のバッファ＝仮置き場）
  occupationTransportPath: 0xa69600, // くすみイエロー（搬送経路：占有していない）
  occupationTransportActive: 0xffe33a, // ブライトイエロー（現在搬送ロボが乗っているタイル）
} as const;

/**
 * 等角菱形タイル（床）を描く。left-top-right-bottom の順。
 * (0,0) を菱形の中心として描画。
 */
export function drawIsoTile(g: Graphics, color: number, border: number | null = null): Graphics {
  const hw = ISO_TILE_W / 2;
  const hh = ISO_TILE_H / 2;
  g.poly([-hw, 0, 0, -hh, hw, 0, 0, hh]);
  g.fill(color);
  if (border !== null) {
    g.stroke({ width: 0.5, color: border, alpha: 0.4 });
  }
  return g;
}

/**
 * 占有タイルの半透明オーバーレイを描画する。
 * 機械ごとに色を変えて「どのタイルを占有しているか」を視認できるようにする。
 */
export function drawOccupationTile(
  g: Graphics,
  color: number,
  fillAlpha: number,
  borderColor: number,
  borderAlpha: number,
): Graphics {
  const hw = ISO_TILE_W / 2;
  const hh = ISO_TILE_H / 2;
  g.poly([-hw, 0, 0, -hh, hw, 0, 0, hh]);
  g.fill({ color, alpha: fillAlpha });
  g.stroke({ width: 0.8, color: borderColor, alpha: borderAlpha });
  return g;
}

/** ベルト面の床面からの高さ（px）。ワーク・プレス機などベルト上の要素もこの分だけ持ち上げる。 */
export const CONVEYOR_BELT_HEIGHT_PX = 6;

/**
 * 等角タイル 1 個分のコンベアセグメントを描画する。
 *
 * 参照: references/conveyor-stripe.png に倣ったクリーンなダークパープル系のデザイン。
 * 構成（描画順 = 奥→手前）:
 *   1. FRONT-LEFT サイドパネル（ダークパープル単色 + 上端の薄ハイライト）
 *   2. FRONT-RIGHT サイドパネル（影側のさらに暗いパープル + 上端ハイライト）
 *   3. ベルト天面: 縁（暗パープル）+ ベルト本体（中間パープル）
 *   4. ベルト天面のオーバーレイ（任意 — buffer / press タイルなどで占有色を重ねる）
 *   5. ベルト中央の薄いハイライト帯（phase でスライドして方向感を控えめに表現）
 *
 * @param phase 0..1 のアニメーション位相
 * @param overlay 任意。{ color, alpha } を渡すとベルト天面（インセット菱形）に色を重ねる
 */
export function drawConveyorSegment(g: Graphics, phase: number, overlay?: { color: number; alpha: number }): Graphics {
  g.clear();
  const hw = ISO_TILE_W / 2;
  const hh = ISO_TILE_H / 2;
  const H = CONVEYOR_BELT_HEIGHT_PX;

  // --- サイドパネル ---
  // FRONT-LEFT
  g.poly([-hw, -H, 0, hh - H, 0, hh, -hw, 0]);
  g.fill(PALETTE.conveyorSideMain);
  // 上端の明るいライン
  g.poly([-hw, -H, 0, hh - H, 0, hh - H + 1, -hw, -H + 1]);
  g.fill(PALETTE.conveyorSideTop);

  // FRONT-RIGHT
  g.poly([0, hh - H, hw, -H, hw, 0, 0, hh]);
  g.fill(PALETTE.conveyorSideShadow);
  // 上端のハイライト
  g.poly([0, hh - H, hw, -H, hw, -H + 1, 0, hh - H + 1]);
  g.fill(PALETTE.conveyorSideMain);

  // --- ベルト天面 ---
  // 縁（外周菱形）— ベルト本体より少し暗い色で枠を表現
  g.poly([0, -hh - H, hw, -H, 0, hh - H, -hw, -H]);
  g.fill(PALETTE.conveyorBeltRim);
  // ベルト本体（インセット菱形）
  const inset = 0.86;
  g.poly([0, -hh * inset - H, hw * inset, -H, 0, hh * inset - H, -hw * inset, -H]);
  g.fill(PALETTE.conveyorBelt);

  // --- 占有色オーバーレイ（press / buffer タイルなど）---
  if (overlay) {
    g.poly([0, -hh * inset - H, hw * inset, -H, 0, hh * inset - H, -hw * inset, -H]);
    g.fill({ color: overlay.color, alpha: overlay.alpha });
  }

  // --- ベルト中央を流れる薄いハイライト帯（控えめな方向感）---
  // 帯はベルト上面に乗り、進行方向に直交した向き（row+ 方向）に長く伸び、
  // 進行方向（col+ 方向）にスライドする。
  // PERP を画面 2D 上の 90° 回転 (-DIRY, DIRX) にすると等角面から外れて
  // ベルトに「沈み込む」ように見えるので、ここでは row+ 方向の単位ベクトルを採用する。
  const DIRX = 0.8944; // col+ 方向（進行）
  const DIRY = 0.4472;
  const PERPX = -0.8944; // row+ 方向（ベルト上面の直交）
  const PERPY = 0.4472;
  const cyclePx = hw * 1.55;
  // -0.5..+0.5 の範囲で 1 本の帯がスライド
  const tNorm = phase % 1;
  const t = (tNorm - 0.5) * cyclePx;
  const cx = DIRX * t;
  const cy = DIRY * t - H;

  // 帯のサイズ
  // ベルト本体（インセット菱形 inset=0.86）に収まるよう lenHalf を制限。
  // 制約: lenHalf <= 0.5 * inset * sqrt(hw² + hh²) ≈ 7.69px。0.8 倍の安全マージンで 6.15px 程度。
  const lenHalf = 5.5; // PERP 方向の半長（ベルト幅に収まる）
  const widthHalf = 0.9; // DIR 方向の厚み（細く控えめに）
  const corners: [number, number][] = [
    [-lenHalf, -widthHalf],
    [lenHalf, -widthHalf],
    [lenHalf, widthHalf],
    [-lenHalf, widthHalf],
  ];
  const pts: number[] = [];
  for (const [lx, ly] of corners) {
    pts.push(cx + lx * PERPX + ly * DIRX, cy + lx * PERPY + ly * DIRY);
  }
  g.poly(pts);
  g.fill({ color: PALETTE.conveyorBeltLight, alpha: 0.55 });

  return g;
}

/** プレス脚＋ビーム共通の寸法定数 */
const PRESS_POST_H = 26;
const PRESS_POST_W = 5;
const PRESS_JOINT_W = 11;
const PRESS_JOINT_H = 5;

/**
 * プレス機の脚（北 / 南）を 1 タイル分のスプライトとして描画するヘルパ。
 * 原点 = その脚タイルの中心。
 * 構成: 影 / 台座 / 柱（縦） / 頂点のジョイントプレート。
 */
function drawPressLeg(g: Graphics): Graphics {
  // 足元の影
  g.ellipse(0, 1.5, 5, 2);
  g.fill({ color: 0x000000, alpha: 0.25 });

  // 台座
  g.roundRect(-7, -3, 14, 7, 2);
  g.fill(PALETTE.pressDark);
  g.roundRect(-7, -3, 14, 2, 2);
  g.fill(PALETTE.pressBase);

  // 柱
  g.roundRect(-PRESS_POST_W / 2, -PRESS_POST_H, PRESS_POST_W, PRESS_POST_H, 1);
  g.fill(PALETTE.pressBase);
  g.roundRect(-PRESS_POST_W / 2 + 0.5, -PRESS_POST_H, 1.5, PRESS_POST_H, 0.5);
  g.fill(0xffa070);
  g.roundRect(PRESS_POST_W / 2 - 1, -PRESS_POST_H, 1, PRESS_POST_H, 0.5);
  g.fill(PALETTE.pressDark);

  // 頂点のジョイントプレート
  g.roundRect(-PRESS_JOINT_W / 2, -PRESS_POST_H - 1, PRESS_JOINT_W, PRESS_JOINT_H, 1.5);
  g.fill(PALETTE.pressDark);
  g.roundRect(-PRESS_JOINT_W / 2, -PRESS_POST_H - 1, PRESS_JOINT_W, 2, 1.5);
  g.fill(PALETTE.pressBase);
  g.circle(-PRESS_JOINT_W / 2 + 1.5, -PRESS_POST_H + 2, 0.7);
  g.fill(0x000000);
  g.circle(PRESS_JOINT_W / 2 - 1.5, -PRESS_POST_H + 2, 0.7);
  g.fill(0x000000);
  return g;
}

/** 北脚（row=9 タイル中心が原点）。 */
export function createPressNorthLeg(): Graphics {
  return drawPressLeg(new Graphics());
}

/** 南脚（row=11 タイル中心が原点）。 */
export function createPressSouthLeg(): Graphics {
  return drawPressLeg(new Graphics());
}

/**
 * プレス機の上部ビーム＋グロー。
 * 原点 = プレスタイル（row=10）の中心。両脚タイル中心へのオフセットを使ってビームを描く。
 */
export function createPressBeam(): Graphics {
  const g = new Graphics();
  const hw = ISO_TILE_W / 2;
  const hh = ISO_TILE_H / 2;

  // 両脚タイル中心への screen オフセット（プレスタイル原点から）
  const northX = hw;
  const northY = -hh;
  const southX = -hw;
  const southY = hh;
  const northTopY = northY - PRESS_POST_H; // -34
  const southTopY = southY - PRESS_POST_H; // -18

  // ビーム中心線の方向と垂直
  const bdx = southX - northX;
  const bdy = southTopY - northTopY;
  const bLen = Math.sqrt(bdx * bdx + bdy * bdy);
  const ux = bdx / bLen;
  const uy = bdy / bLen;
  const perpX = -uy;
  const perpY = ux;

  const beamThick = 7;
  const ht = beamThick / 2;

  // 4 つの平行四辺形コーナー
  const tnX = northX + perpX * ht;
  const tnY = northTopY + perpY * ht;
  const tsX = southX + perpX * ht;
  const tsY = southTopY + perpY * ht;
  const bsX = southX - perpX * ht;
  const bsY = southTopY - perpY * ht;
  const bnX = northX - perpX * ht;
  const bnY = northTopY - perpY * ht;

  g.poly([tnX, tnY, tsX, tsY, bsX, bsY, bnX, bnY]);
  g.fill(PALETTE.pressDark);

  // 上面ハイライト
  const hht = 2;
  const thnX = northX + perpX * (ht - hht);
  const thnY = northTopY + perpY * (ht - hht);
  const thsX = southX + perpX * (ht - hht);
  const thsY = southTopY + perpY * (ht - hht);
  g.poly([tnX, tnY, tsX, tsY, thsX, thsY, thnX, thnY]);
  g.fill(PALETTE.pressBase);

  // 下端の暗ライン
  const bottomLineHt = 1;
  const bnXh = northX - perpX * (ht - bottomLineHt);
  const bnYh = northTopY - perpY * (ht - bottomLineHt);
  const bsXh = southX - perpX * (ht - bottomLineHt);
  const bsYh = southTopY - perpY * (ht - bottomLineHt);
  g.poly([bnXh, bnYh, bsXh, bsYh, bsX, bsY, bnX, bnY]);
  g.fill({ color: 0x000000, alpha: 0.35 });

  // インジケータグロー
  const beamMidX = (northX + southX) / 2;
  const beamMidY = (northTopY + southTopY) / 2;
  g.circle(beamMidX + perpX * (ht * 0.6), beamMidY + perpY * (ht * 0.6), 1.4);
  g.fill(PALETTE.pressGlow);

  return g;
}

/**
 * プレスヘッド（上下動するハンマー）。原点 = シャフト上端中央 (= ビームに接する位置)。
 *
 * 構造（描画は y=0 から下方向に伸びる）:
 *   - シャフト: 細い縦バー (アイドル時もビーム下に常に見える)
 *   - ハンマー面: シャフト末端の幅広な打撃面 (ワーク打撃時にここがワーク天面に当たる)
 *
 * renderer 側でこの Container 全体の y を上下させて打ち下ろしを表現する。
 * 原点をシャフト上端にしたのは、ビームから「降りてくる」見た目にするため。
 */
export function createPressHead(): Graphics {
  const g = new Graphics();
  const shaftW = 4;
  const shaftH = 6;
  const faceW = 14;
  const faceH = 4;
  // シャフト
  g.roundRect(-shaftW / 2, 0, shaftW, shaftH, 1);
  g.fill(PALETTE.pressHead);
  g.roundRect(-shaftW / 2, 0, shaftW, 1.5, 1);
  g.fill(0x4a4a56);
  // ハンマー面
  g.roundRect(-faceW / 2, shaftH, faceW, faceH, 1);
  g.fill(PALETTE.pressHead);
  g.roundRect(-faceW / 2, shaftH, faceW, 1.2, 1);
  g.fill(0x4a4a56);
  return g;
}

/**
 * ロボットアーム本体（ピック&プレースの土台）。
 */
export function createArmBase(): Graphics {
  const g = new Graphics();
  const hw = ISO_TILE_W / 2;
  const hh = ISO_TILE_H / 2;

  // 影 — 等角菱形タイル 1 個に収まるよう、中心を菱形中央寄りに上げて小さく描く
  g.ellipse(0, hh * 0.35, hw * 0.45, hh * 0.3);
  g.fill({ color: 0x000000, alpha: 0.22 });

  // ベース
  g.roundRect(-hw * 0.65, -6, hw * 1.3, 12, 3);
  g.fill(PALETTE.armBase);
  g.roundRect(-hw * 0.65, -6, hw * 1.3, 4, 3);
  g.fill(0xff9a6b);

  // 軸
  g.circle(0, -6, 3);
  g.fill(PALETTE.armJoint);
  return g;
}

/**
 * ロボットアーム（回転する腕）。
 */
export function createArmReach(): Graphics {
  const g = new Graphics();
  // 腕（原点＝根元の回転中心）
  g.roundRect(-2, -18, 4, 20, 2);
  g.fill(PALETTE.armBase);
  // 先端グリップ
  g.circle(0, -18, 3);
  g.fill(PALETTE.armGrip);
  return g;
}

/**
 * 搬送ロボット（AGV）本体。
 */
export function createTransportRobot(): Graphics {
  const g = new Graphics();
  const hw = ISO_TILE_W / 2;
  const hh = ISO_TILE_H / 2;

  // 影 — 等角菱形タイル 1 個に収まるように小さくする。
  // 旧: ellipse(0, hh*0.9, hw*0.8, hh*0.5) は y が下端寄りで幅が大きく、
  //     菱形の細い部分から大きくはみ出して隣タイルを暗く見せていた。
  // 新: 中心を菱形中央寄りに上げて、x/y 半径を縮小し菱形内に収める。
  g.ellipse(0, hh * 0.35, hw * 0.45, hh * 0.3);
  g.fill({ color: 0x000000, alpha: 0.22 });

  // 車体（下層）
  g.roundRect(-hw * 0.75, -8, hw * 1.5, 10, 3);
  g.fill(PALETTE.robotDark);
  g.roundRect(-hw * 0.75, -8, hw * 1.5, 4, 3);
  g.fill(PALETTE.robotBase);

  // 荷台の縁
  g.roundRect(-hw * 0.75, -9, hw * 1.5, 2, 1);
  g.fill(0xffeb3b);

  // キャビン（小さいドーム）
  g.roundRect(-5, -14, 10, 6, 2);
  g.fill(PALETTE.robotCab);
  g.circle(-2, -11, 1);
  g.fill(0x90caf9);

  return g;
}

/**
 * ワーク（加工対象物）。状態で色が変わる。
 */
export function createWorkSprite(kind: "idle" | "pressed" | "done"): Graphics {
  const g = new Graphics();
  const color = kind === "idle" ? PALETTE.workIdle : kind === "pressed" ? PALETTE.workPressed : PALETTE.workDone;
  g.roundRect(-5, -7, 10, 10, 2);
  g.fill(color);
  g.roundRect(-5, -7, 10, 3, 2);
  g.fill({ color: 0xffffff, alpha: 0.25 });
  return g;
}

/**
 * 積み下ろしエリアのハイライト。
 */
export function createUnloadZone(): Graphics {
  const g = new Graphics();
  const hw = ISO_TILE_W / 2;
  const hh = ISO_TILE_H / 2;
  g.poly([-hw, 0, 0, -hh, hw, 0, 0, hh]);
  g.fill({ color: PALETTE.unloadZone, alpha: 0.45 });
  g.stroke({ width: 1, color: PALETTE.unloadZone, alpha: 0.9 });
  return g;
}
